import React, { useState } from 'react';
import { PerformanceGlanceCard } from './dashboard/ProgressOverviewCard';
import { UpcomingTestCard } from './dashboard/UpcomingTestCard';
import { RecentPerformanceCard } from './dashboard/RecentPerformanceCard';
import { QuickPracticeCard } from './dashboard/QuickPracticeCard';
import { LeaderboardCard } from './dashboard/LeaderboardCard';
import { AnnouncementsCard } from './dashboard/AnnouncementsCard';
import { RecentAchieversCard } from './dashboard/RecentAchieversCard';
import { BookmarksView } from './dashboard/BookmarksView';
import { TestHistoryCard } from './dashboard/TestHistoryCard';
import { KnowledgeSprint } from './KnowledgeSprint';
import { AdaptiveTest } from './AdaptiveTest';
import { LayoutDashboardIcon } from './icons/InterfaceIcons';

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
};

const UserInfoPill = ({ label, value }) => (
    value ? (
        <div className="bg-brand-surface border border-brand-border rounded-full px-4 py-2 text-sm">
            <span className="font-semibold text-brand-text-secondary">{label}: </span>
            <span className="font-bold text-brand-text-primary">{value}</span>
        </div>
    ) : null
);

export const Dashboard = ({ user, examResults, onToggleBookmark }) => {
    const [activeView, setActiveView] = useState('overview');
    const [showKnowledgeSprint, setShowKnowledgeSprint] = useState(false);
    const [showAdaptiveTest, setShowAdaptiveTest] = useState(false);
    const [adaptiveTestSubject, setAdaptiveTestSubject] = useState('Mathematics');

    const OverviewContent = () => (
        <>
            {/* User Details from Onboarding */}
            <div className="flex flex-wrap gap-3 mb-8">
                <UserInfoPill label="Target Exam" value={user.exam} />
                <UserInfoPill label="Class" value={user.class} />
                <UserInfoPill label="Target Year" value={user.targetYear} />
                <UserInfoPill label="Phone" value={user.phone} />
            </div>

            {/* Main Dashboard Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <div className="lg:col-span-2 xl:col-span-2">
                    <PerformanceGlanceCard completedTests={user.tests?.completed} />
                </div>
                <UpcomingTestCard assignedTests={user.tests?.assigned} />
                <TestHistoryCard completedTests={user.tests?.completed} />
                <div className="lg:col-span-2">
                    <RecentPerformanceCard completedTests={user.tests?.completed}/>
                </div>
                <QuickPracticeCard />
            </div>
            
            {/* Recent Achievers Section */}
            <div className="mt-8">
                <RecentAchieversCard examResults={examResults} />
            </div>

            {/* Game Zone */}
            <div className="mt-8">
                <h2 className="text-2xl font-bold text-brand-text-primary mb-4">🎮 Challenge Zone</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-6 text-white">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-xl font-bold">⚡ Knowledge Sprint</h3>
                                <p className="text-purple-100">Race against time with adaptive difficulty</p>
                            </div>
                            <div className="text-3xl">🏃‍♂️</div>
                        </div>
                        <button
                            onClick={() => setShowKnowledgeSprint(true)}
                            className="w-full px-4 py-2 bg-white text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors"
                        >
                            Start Sprint
                        </button>
                    </div>
                    
                    <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-6 text-white">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-xl font-bold">🎯 Adaptive Test</h3>
                                <p className="text-blue-100">AI-powered personalized assessment</p>
                            </div>
                            <div className="text-3xl">🤖</div>
                        </div>
                        <div className="flex space-x-2">
                            <select
                                value={adaptiveTestSubject}
                                onChange={(e) => setAdaptiveTestSubject(e.target.value)}
                                className="flex-1 px-3 py-2 text-blue-600 rounded-lg text-sm"
                            >
                                <option value="Mathematics">Mathematics</option>
                                <option value="Physics">Physics</option>
                                <option value="Chemistry">Chemistry</option>
                            </select>
                            <button
                                onClick={() => setShowAdaptiveTest(true)}
                                className="px-4 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                            >
                                Start
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Secondary Section */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnnouncementsCard />
                <LeaderboardCard />
            </div>
        </>
    );
    
    const bookmarkCount = Object.keys(user.bookmarks?.questions || {}).length;

    return (
        <div className="animate-fade-in">
            {/* Hero / Welcome Block */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-brand-text-primary">
                    {getGreeting()}, {user.fullName?.split(' ')[0] || user.name?.split(' ')[0] || 'User'}!
                </h1>
                <p className="text-lg text-brand-text-secondary mt-1">
                    Ready to crush your preparation today?
                </p>
            </div>
            
            {/* Dashboard Navigation */}
            <div className="border-b border-brand-border mb-6">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                    <button
                        onClick={() => setActiveView('overview')}
                        className={`flex items-center space-x-2 whitespace-nowrap py-3 px-1 border-b-2 font-semibold text-sm transition-colors ${
                            activeView === 'overview'
                                ? 'border-brand-primary text-brand-primary'
                                : 'border-transparent text-brand-text-secondary hover:border-gray-300 hover:text-brand-text-primary'
                        }`}
                    >
                        <LayoutDashboardIcon className="w-5 h-5" />
                        <span>Overview</span>
                    </button>
                    <button
                        onClick={() => setActiveView('bookmarks')}
                        className={`whitespace-nowrap py-3 px-1 border-b-2 font-semibold text-sm transition-colors ${
                            activeView === 'bookmarks'
                                ? 'border-brand-primary text-brand-primary'
                                : 'border-transparent text-brand-text-secondary hover:border-gray-300 hover:text-brand-text-primary'
                        }`}
                    >
                        My Bookmarks
                        {bookmarkCount > 0 && (
                             <span className="ml-2 inline-block py-0.5 px-2 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-primary">
                                {bookmarkCount}
                             </span>
                        )}
                    </button>
                </nav>
            </div>
            
            {/* Conditional Content */}
            {activeView === 'overview' ? (
                <OverviewContent />
            ) : (
                <BookmarksView user={user} onToggleBookmark={onToggleBookmark} />
            )}
            
            {/* Game Modals */}
            {showKnowledgeSprint && (
                <KnowledgeSprint onClose={() => setShowKnowledgeSprint(false)} />
            )}
            
            {showAdaptiveTest && (
                <AdaptiveTest
                    subject={adaptiveTestSubject}
                    onClose={() => setShowAdaptiveTest(false)}
                    onComplete={(results) => {
                        console.log('Adaptive test results:', results);
                        setShowAdaptiveTest(false);
                        // Handle results - could save to database, show results modal, etc.
                    }}
                />
            )}

        </div>
    );
};